import { gql, useQuery, useReactiveVar } from '@apollo/client';
import { Box, CircularProgress } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { allViewsMin } from 'src/api/viewModels/allViews';
import { SearchFilterBar } from 'src/components/SearchFilterBar';
import { FD_MEDIA_GROUP_SLUG, GALLERY_SIZE, HOME_PATH, VIEW_CONTEXT } from 'src/constants';
import { FilterPopoverProvider } from 'src/context/FilterPopoverContext';
import { useLibrarySearchResults } from 'src/context/LibrarySearchResultsContext';
import { Collection } from 'src/dynamoQueries/collections';
import AkomiMediaCardGrid from 'src/HomePage/DrawerAndPushedContents/PushedContents/AkomiMediaCardGrid/AkomiMediaCardGrid';
import TopNavBar from 'src/HomePage/TopNavBar/TopNavBar';
import { getOwner } from 'src/helpers';
import { cleanPayload, getMetadataFieldLabel } from 'src/helpers/common';
import { resetGalleryScope } from 'src/helpers/filters';
import { useResetSelectedState } from 'src/hooks/globalState';
import { useCustomToast } from 'src/hooks/menu';
import { useMetaQ } from 'src/hooks/meta';
import { useFilterPersistence } from 'src/hooks/useFilterPersistence';
import viewStateVar from 'src/store/viewState';
import type { GallerySearchParams } from 'src/utils/searchSchemas';
import GalleryHeaderCompact from './GalleryHeaderCompact';
import GalleryHero from './GalleryHero';
import GalleryInfoPanel from './GalleryInfoPanel';
import GalleryToolbar from './GalleryToolbar';

const PANEL_WIDTH = 380;

interface Owner {
  firstName?: string;
  lastName?: string;
  user?: {
    username: string;
  };
}

interface CollectionData {
  collection: {
    id: string;
    name: string;
    owner: Owner;
    createdAt?: string;
    modifiedAt?: string;
    mediaGroup?: {
      id?: string;
      name?: string;
    };
  };
}

interface ViewFieldEdge {
  node: {
    field: {
      slug: string;
    };
  };
}

interface ViewModel {
  viewfieldSet?: {
    edges?: ViewFieldEdge[];
  };
}

interface SortViewModelData {
  allViews: {
    edges: Array<{
      node: ViewModel;
    }>;
  };
}

const styles = makeStyles(() => ({
  mainContent: {
    transition: 'margin-right 0.3s ease',
  },
  mainContentWithPanel: {
    marginRight: PANEL_WIDTH,
  },
}));

export const GalleryPage = (): React.JSX.Element => {
  const navigate = useNavigate();
  const resetSelectedState = useResetSelectedState();
  const classes = styles();
  const params = useParams({ strict: false });
  // Get typed search params from TanStack Router - this is the source of truth
  const search = useSearch({ strict: false }) as GallerySearchParams;
  const { toastr } = useCustomToast();
  const viewState = useReactiveVar(viewStateVar);
  const { trueTotalCount } = useLibrarySearchResults();
  const id = (params as { galleryId?: string }).galleryId || '';
  const heroRef = useRef<HTMLDivElement>(null);

  // Initialize filter persistence for gallery scope with gallery-specific storage
  useFilterPersistence(VIEW_CONTEXT.GALLERY, id);
  const { data: metaData, error: metaDataError } = useMetaQ(
    {
      variables: {
        globalId: id,
        definitionSlugIn: [FD_MEDIA_GROUP_SLUG],
      },
    },
    false,
  );

  if (metaDataError) {
    toastr.error('Error fetching gallery media group');
  }

  const buildQuery = () => {
    const query = Collection(true);
    return gql(jsonToGraphQLQuery(query, { pretty: true }));
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: resetSelectedState is stable, runs only on mount
  useEffect(() => {
    resetSelectedState();
  }, []);

  // Fetch sort view model to derive a sensible default slug if state lacks one
  const { data: sortVm } = useQuery<SortViewModelData>(allViewsMin, {
    variables: { viewName: 'Library', componentName: 'MediaSortByMenu' },
    fetchPolicy: 'cache-and-network',
  });

  // Ensure a sort is present when entering a Gallery; default to desc.
  // Prefer current global state slug; otherwise fall back to the first view model field.
  // biome-ignore lint/correctness/useExhaustiveDependencies: sortVm is intentionally excluded to prevent re-running on every sortVm change
  useEffect(() => {
    // If sort is already set in URL, skip
    if (search.sort) return;

    const sd = viewStateVar().sortData;
    let slug: string | undefined = sd?.sortBy?.value;
    let order = sd?.orderBy || 'desc';

    if (!slug && sortVm) {
      const vm = cleanPayload(sortVm)?.[0] as ViewModel | undefined;
      const edge = vm?.viewfieldSet?.edges?.[0];
      slug = edge?.node?.field?.slug;
      order = 'desc';
    }

    if (slug) {
      // Use navigate with search object for type-safe URL updates
      navigate({
        search: ((prev: Record<string, unknown>) => ({
          ...prev,
          sort: `${slug}:${order}`,
          // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
        })) as any,
      });
    }
  }, [navigate, search.sort]);

  const { data, loading, error, refetch } = useQuery<CollectionData>(buildQuery(), {
    skip: !id,
    variables: { id },
    fetchPolicy: 'cache-and-network',
  });

  const isPanelOpen = viewState.isGalleryPanelOpen;

  const handlePanelClose = useCallback(() => {
    viewStateVar({
      ...viewStateVar(),
      isGalleryPanelOpen: false,
    });
  }, []);

  const handleUpdateComplete = useCallback(() => {
    refetch();
  }, [refetch]);

  if (loading) {
    return (
      <div>
        <CircularProgress color="secondary" />
      </div>
    );
  }

  if (error) return <div>An Error occurred loading this Gallery</div>;

  // media_group is stored as a direct FK on Collection (not a MetadataField), so
  // read the name off the collection payload rather than the metadata query.
  const mediaGroup = data?.collection?.mediaGroup?.name;
  const owner = getOwner(data?.collection?.owner);
  const galleryName = data?.collection?.name || 'Gallery';
  const galleryPlaceholder = data?.collection?.name ? `Search in ${data.collection.name}...` : undefined;
  const totalItems = trueTotalCount ?? 0;

  // Dynamic field label — falls back to a sensible default if FieldDef.name is missing
  const mediaGroupLabel =
    (metaData ? getMetadataFieldLabel(metaData as Record<string, unknown>, FD_MEDIA_GROUP_SLUG) : undefined) ||
    'Media Group';

  const metaItems = [
    { label: 'Created by', value: owner || '—' },
    { label: mediaGroupLabel, value: mediaGroup || '—' },
  ];

  const breadcrumbItems = [
    {
      label: 'Library',
      onClick: () => {
        // Navigate to clean Library URL — useFilterPersistence on the Library page will
        // restore filters/search/page from localStorage automatically.
        navigate({ to: HOME_PATH });
      },
    },
    {
      label: galleryName,
      onClick: () => {
        resetGalleryScope({ size: GALLERY_SIZE, preserveSort: true, galleryId: id });
        navigate({
          to: `${HOME_PATH}/gallery/${id}`,
          search: { sort: search.sort, page: 1, size: GALLERY_SIZE },
        });
      },
    },
  ];

  return (
    // Provider wraps both main content and GalleryInfoPanel — panel currently does not consume popover state.
    <FilterPopoverProvider>
      <TopNavBar />
      <Box className={`${classes.mainContent} ${isPanelOpen ? classes.mainContentWithPanel : ''}`}>
        <GalleryHero
          ref={heroRef}
          name={galleryName}
          totalItems={totalItems}
          metaItems={metaItems}
          breadcrumbItems={breadcrumbItems}
        />
        <SearchFilterBar
          placeholder={galleryPlaceholder}
          leadingSlot={<GalleryHeaderCompact observeRef={heroRef} name={galleryName} totalItems={totalItems} />}
        />
        <GalleryToolbar galleryId={id} />
        <div>
          <DndProvider backend={HTML5Backend}>
            <AkomiMediaCardGrid />
          </DndProvider>
        </div>
      </Box>
      <GalleryInfoPanel
        isOpen={isPanelOpen}
        onClose={handlePanelClose}
        galleryId={id}
        galleryName={data?.collection?.name || ''}
        owner={owner || ''}
        totalItems={totalItems}
        dateCreated={data?.collection?.createdAt}
        lastModified={data?.collection?.modifiedAt}
        onUpdateComplete={handleUpdateComplete}
      />
    </FilterPopoverProvider>
  );
};
